import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { finishTutorial, getTutorial } from "../services/admin";
import "./tutorial.css";

// The store owner's first sign-in tour (on unless a Super Admin turned it off: Settings -> Store owner tutorial).
// It must be finished once: there is no skip. Each step highlights a menu item (on phones, the tab bar or More) or a
// part of the page, opening the tab it belongs to first. TutorialButton (top bar, beside the alerts) plays it again
// any time; a replay can be closed.
const STEPS = [
  { tab: "Overview", nav: "Overview", title: "Welcome to your admin panel 👋", text: "This short tour shows you how to run your store. Overview is your home: your orders, websites and what to do next." },
  { tab: "Overview", target: ".overview-stats", title: "Your numbers at a glance", text: "Websites, plans and your orders (total, pending, in transit and delivered) are counted here." },
  { tab: "Order management", nav: "Order management", title: "Order management", text: "Every order from your customers comes here. Pick a website at the top to see its orders." },
  { tab: "Order management", target: ".mo-table tbody tr:first-child, .mo-cards > :first-child, .orders-empty", title: "Accept, ship and track orders", text: "A new order rings with a bell until you accept or reject it. Then update its status as it is packed, shipped and delivered, and message the customer on WhatsApp." },
  { tab: "My websites", nav: "My websites", title: "My websites", text: "Your websites, their plan and expiry date. A red number shows orders still to send out." },
  { tab: "My websites", target: ".site-table .edit-action", title: "Edit your website", text: "Tap Edit site to change your logo, colours, banners and products, and see the changes live before you save." },
  { tab: "My websites", target: ".site-table .orders-action", title: "Manage orders from here too", text: "Manage orders opens the orders of this website." },
  { tab: "Store settings", nav: "Store settings", title: "Store settings", text: "Your business details, shipping charges, GST and how customers pay. Each website has its own settings." },
  { tab: "Store settings", target: "#store-business", title: "Business information", text: "Your name, store name, email, contact number and address. Shoppers see them on your Contact us page and in their order emails. Your email from sign-up is already filled in." },
  { tab: "Store settings", target: "#store-shipping", title: "Shipping & tax", text: "Delivery fee, the order amount above which delivery is free, and your GST rate. A new store starts with all three at 0, so nothing extra is added at checkout until you set them." },
  { tab: "Store settings", target: "#store-payment", title: "UPI payments (Razorpay)", text: "Take UPI payments into your own Razorpay account. Add your Razorpay Key ID and Key secret, and each payment is confirmed automatically." },
  { tab: "Store settings", target: "#store-manual-upi", title: "Manual UPI payments", text: "No gateway, no fees: shoppers pay your UPI ID and send the order on WhatsApp. You check the payment and tap Mark as paid in Order management." },
  { tab: "Staff", nav: "Staff", title: "Staff", text: "Add people who help you, and choose what each can do: orders, products or settings." },
  { tab: "Data migration", nav: "Data migration", title: "Data migration", text: "Bring your products from another website or a spreadsheet in one go." },
  { tab: "Billing", nav: "Billing", title: "Billing", text: "Pay for your plan and see your payments. Keep your plan active so your website stays online." },
  { tab: "Overview", nav: "Overview", title: "You're ready! 🎉", text: "That's everything. Tap the 🎓 button at the top to see this tour again, or Need help to chat with the Ownezzy team on WhatsApp." },
];
const PAD = 6;
const START_EVENT = "ownezzy:start-tutorial";

// The 🎓 button in the top bar: plays the tour again from the start
export function TutorialButton() {
  return (
    <button type="button" className="alert-bell tutorial-btn" onClick={() => window.dispatchEvent(new Event(START_EVENT))} aria-label="Show the tutorial again" title="Tutorial">
      <span aria-hidden="true">🎓</span>
    </button>
  );
}

// The element to highlight: the step's own target, or its menu item where it can be seen (sidebar, tab bar, More)
function findTarget(step) {
  const visible = (el) => el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden";
  if (step.target) return [...document.querySelectorAll(step.target)].find(visible) || null;
  const items = [...document.querySelectorAll(`[data-tour="${step.nav}"]`)].filter(visible);
  return items[0] || [...document.querySelectorAll('[data-tour="More"]')].find(visible) || null;
}

export default function Tutorial({ setTab }) {
  const [show, setShow] = useState(false);
  const [replay, setReplay] = useState(false); // started from TutorialButton, so it can be closed
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const step = STEPS[index];

  useEffect(() => {
    getTutorial()
      .then(({ data }) => {
        setShow(data.show);
        if (!data.show) window.dispatchEvent(new Event("ownezzy:tutorial-finished"));
      })
      .catch(() => window.dispatchEvent(new Event("ownezzy:tutorial-finished")));
    const start = () => { setIndex(0); setReplay(true); setShow(true); };
    window.addEventListener(START_EVENT, start);
    return () => window.removeEventListener(START_EVENT, start);
  }, []);

  // Opens the step's tab, then finds what to highlight once the tab has drawn (data may still be loading)
  useEffect(() => {
    if (!show) return undefined;
    setTab(step.tab);
    let tries = 0;
    let timer;
    const look = () => {
      const el = findTarget(step);
      if (el) {
        // A tall card (Store settings) starts at its top; anything else is centred
        el.scrollIntoView({ block: el.offsetHeight > window.innerHeight * 0.6 ? "start" : "center", behavior: "smooth" });
        timer = setTimeout(() => setRect(el.getBoundingClientRect()), 350);
      } else if (tries++ < 12) {
        timer = setTimeout(look, 250);
      } else {
        setRect(null); // nothing to point at: the card shows in the middle
      }
    };
    setRect(null);
    timer = setTimeout(look, 200);
    return () => clearTimeout(timer);
  }, [show, step, setTab]);

  // Follows the highlighted element when the page scrolls or resizes
  const follow = useCallback(() => {
    const el = findTarget(step);
    if (el) setRect(el.getBoundingClientRect());
  }, [step]);
  useLayoutEffect(() => {
    if (!show) return undefined;
    window.addEventListener("resize", follow);
    window.addEventListener("scroll", follow, true);
    return () => { window.removeEventListener("resize", follow); window.removeEventListener("scroll", follow, true); };
  }, [show, follow]);

  if (!show) return null;
  const last = index === STEPS.length - 1;
  const finish = () => {
    setShow(false);
    if (!replay) {
      finishTutorial().catch(() => {});
      window.dispatchEvent(new Event("ownezzy:tutorial-finished"));
    }
    setReplay(false);
    setTab("Overview");
  };

  // The card goes below the highlight when there is room, otherwise above it; centred when nothing is highlighted
  const cardStyle = {};
  if (rect) {
    const width = Math.min(340, window.innerWidth - 32);
    cardStyle.width = width;
    if (rect.width < 300 && rect.right + width + 32 < window.innerWidth) {
      // A narrow item with room on its right (the sidebar menu): beside it, so the rest of the menu stays visible
      cardStyle.left = rect.right + PAD + 14;
      cardStyle.top = Math.max(16, Math.min(rect.top - 10, window.innerHeight - 240));
    } else {
      cardStyle.left = Math.max(16, Math.min(rect.left, window.innerWidth - width - 16));
      if (rect.bottom + 230 < window.innerHeight) cardStyle.top = rect.bottom + PAD + 10;
      else if (rect.top > 250) cardStyle.bottom = window.innerHeight - rect.top + PAD + 10;
      else cardStyle.bottom = 16; // taller than the screen: the card sits at the bottom, over the highlight
    }
  }

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label="Admin panel tour">
      {rect ? (
        <div className="tour-spot" style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }} />
      ) : (
        <div className="tour-shade" />
      )}
      <div className={`tour-card ${rect ? "" : "is-centred"}`} style={cardStyle}>
        <span className="tour-count">Step {index + 1} of {STEPS.length}</span>
        <h3>{step.title}</h3>
        <p>{step.text}</p>
        <div className="tour-progress" aria-hidden="true"><span style={{ width: `${((index + 1) / STEPS.length) * 100}%` }} /></div>
        <div className="tour-actions">
          {replay && !last && <button className="tour-back tour-close" onClick={finish}>Close</button>}
          {index > 0 && <button className="tour-back" onClick={() => setIndex(index - 1)}>Back</button>}
          <button className="tour-next" onClick={() => (last ? finish() : setIndex(index + 1))} autoFocus>{last ? "Start using Ownezzy" : "Next"}</button>
        </div>
      </div>
    </div>
  );
}
